import { Box, Typography, Stack, List, IconButton, useTheme, StackProps } from '@mui/material';
import { useState } from 'react';
import { ErrorCode, FileRejection } from 'react-dropzone';

import UploadedFileListItem from '../uploader/UploadedFileListItem';

import UploadFilledIcon from '@/assets/icons/upload-filled.svg?react';
import UploadOutlinedIcon from '@/assets/icons/upload-outlined.svg?react';
import {
  FilePicker,
  DEFAULT_MAX_FILE_SIZE_BYTES,
  DEFAULT_MAX_FILE_COUNT,
} from '@/components/common/FilePicker';
import { Document } from '@/models/Document';
import { useTranslationPrefix } from '@/translations/i18n';

const MAX_FILE_SIZE_BYTES = DEFAULT_MAX_FILE_SIZE_BYTES;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / (1024 * 1024);

export interface FilePickerWithListProps extends StackProps {
  onFilesDrop: (files: File[]) => void;
  uploadedFiles: Document[];
  maxFileCount?: number;
  onSubmit?: () => void;
  onDelete: (file: Document) => void;
}

export const FilePickerWithList = ({
  onFilesDrop,
  uploadedFiles,
  maxFileCount,
  onSubmit,
  onDelete,
  ...rest
}: FilePickerWithListProps) => {
  const _theme = useTheme();
  const t = useTranslationPrefix('uploader');
  const isAtLeastOneFileUploaded = uploadedFiles.length > 0;

  const [fileRejectionError, setFileRejectionError] = useState('');

  const mapErrorCodeToTranslation = (code: string) => {
    if (code === ErrorCode.FileInvalidType) {
      return t('file_rejection.invalid_file_type');
    }
    if (code === ErrorCode.FileTooLarge) {
      return t('file_rejection.file_too_large', { maxInMB: MAX_FILE_SIZE_MB });
    }
    if (code === ErrorCode.TooManyFiles) {
      return t('file_rejection.too_many_files', { count: maxFileCount ?? DEFAULT_MAX_FILE_COUNT });
    }
    return t('file_rejection.unknown');
  };

  const handleOnFilesDrop = (files: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      setFileRejectionError(mapErrorCodeToTranslation(fileRejections[0].errors[0].code));
      return;
    }
    setFileRejectionError('');
    onFilesDrop(files);
  };

  return (
    <Stack spacing={4} flexGrow={1} alignItems="center" width="100%" {...rest}>
      <FilePicker
        withDropZone
        maxFileCount={maxFileCount}
        maxFileSizeInBytes={MAX_FILE_SIZE_BYTES}
        onChange={handleOnFilesDrop}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-around"
        width="100%"
        color="primary.main"
        paddingX={2}>
        <Typography fontSize={10}>{t('supported_file_types')}</Typography>
        <Typography fontSize={10}>{t('max_file_size', { maxInMB: MAX_FILE_SIZE_MB })}</Typography>
      </Stack>

      {fileRejectionError && (
        <Typography variant="body2" color="error.main" textAlign="center">
          {fileRejectionError}
        </Typography>
      )}

      {uploadedFiles.length > 0 && (
        <Box sx={{ maxHeight: 300, overflow: 'auto', width: '100%' }}>
          <List>
            {uploadedFiles.map((uploadedFile) => (
              <UploadedFileListItem key={uploadedFile.id} file={uploadedFile} onDelete={onDelete} />
            ))}
          </List>
        </Box>
      )}

      {onSubmit && (
        <IconButton
          onClick={() => {
            if (isAtLeastOneFileUploaded) {
              onSubmit();
            }
          }}>
          {isAtLeastOneFileUploaded ? (
            <UploadFilledIcon width={52} height={52} fill="#c0ff02" />
          ) : (
            <UploadOutlinedIcon width={52} height={52} />
          )}
        </IconButton>
      )}
    </Stack>
  );
};
